/* Dialog shell for YouTube subscription import.
   Routes to sub-views based on the current step from useYouTubeImport. */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import type { useYouTubeImport } from "@/hooks/useYouTubeImport";
import { YouTubeConnectPrompt } from "./YouTubeConnectPrompt";
import { YouTubeChannelList } from "./YouTubeChannelList";
import { YouTubeImportResults } from "./YouTubeImportResults";

type YouTubeImportState = ReturnType<typeof useYouTubeImport>;

interface YouTubeImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  importState: YouTubeImportState;
}

export function YouTubeImportDialog({
  open,
  onOpenChange,
  importState,
}: YouTubeImportDialogProps) {
  const {
    step,
    channels,
    selectedIds,
    existingChannelIds,
    importResults,
    error,
    startOAuth,
    toggleChannel,
    selectAll,
    clearSelection,
    confirmImport,
    reset,
  } = importState;

  const handleClose = () => {
    onOpenChange(false);
    if (step === "results") {
      reset();
    }
  };

  const title =
    step === "connect"
      ? "YouTube"
      : step === "fetching"
        ? "Loading subscriptions..."
        : step === "selecting" || step === "importing"
          ? "Select channels"
          : "Import complete";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {step === "connect" && (
          <YouTubeConnectPrompt
            onConnect={startOAuth}
            isLoading={false}
            error={error}
          />
        )}

        {step === "fetching" && (
          <div className="flex flex-col items-center gap-3 py-8">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Fetching your subscriptions...
            </p>
          </div>
        )}

        {(step === "selecting" || step === "importing") && (
          <YouTubeChannelList
            channels={channels}
            selectedIds={selectedIds}
            existingChannelIds={existingChannelIds}
            isImporting={step === "importing"}
            onToggle={toggleChannel}
            onSelectAll={selectAll}
            onClearSelection={clearSelection}
            onImport={confirmImport}
            onCancel={handleClose}
          />
        )}

        {step === "results" && importResults && (
          <YouTubeImportResults
            imported={importResults.imported}
            failed={importResults.failed}
            onDone={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
